import { useState, useMemo, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  InputAdornment,
  useMediaQuery,
  IconButton,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import {
  Share,
  ContentCopy,
  Download,
  Close,
  CloudUpload,
  Search,
} from "@mui/icons-material";
import PublicIcon from "@mui/icons-material/Public";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation } from "react-router-dom";
import { en } from "../translations/en";
import { AppButton } from "../components/AppButton";
import { useBookmarks } from "../hooks/useBookmarks";
import { useAuth } from "../hooks/useAuth";
import { useCollections } from "../hooks/useCollections";
import type { CollectionWithRole } from "../types/collection";
import { useActiveCatalog } from "../hooks/useActiveCatalog";
import { logger } from "../utils/logger";
import {
  loadMyObjectsSortPreference,
  saveMyObjectsSortPreference,
  MY_OBJECTS_SORT_OPTIONS,
  SortOption,
  toSearchSortBy,
} from "../utils/sortUtils";
import { SortSelector } from "../components/SortSelector";
import {
  CatalogGridView,
  CatalogListView,
  CatalogImageView,
  ViewModeToggle,
  loadViewMode,
  saveViewMode,
} from "../components/catalog-views";
import type { ViewMode } from "../components/catalog-views";
import { BulkActionsToolbar } from "../components/BulkActionsToolbar";
import { BulkEditDialog } from "../components/BulkEditDialog";
import type { BulkEditResponse } from "../utils/bulkEditApi";
import { useSelection } from "../hooks/useSelection";
import { handleClearedItemNavState } from "./myObjectsNavState";
import { updateVisibilityBulk } from "../utils/visibilityApi";

import { DisplayNameNotice } from "../components/DisplayNameNotice";
import { useUserProfile } from "../hooks/useUserProfile";
import { getArtistDisplayName } from "../utils/displayName";
import { ExportDialog } from "../components/export/ExportDialog";
import { fetchPdfData } from "../utils/exportApi";
import { generateCatalogPdf, PdfExportItem, type PdfProgressCallback } from "../utils/pdfExport";
import { useBrowse } from "../hooks/useBrowse";
import { getCatalogDisplayName } from "../utils/stringHelpers";
import type { BrowseNavQuery } from "../types/browseNavigation";
import { CatalogPagination } from "../components/CatalogPagination";
import { featureFlags } from "../utils/featureFlags";
import { useCatalogFacetFilters, ALL_CATALOG_FACETS } from "../hooks/useCatalogFacetFilters";
import { CatalogFilterRail } from "../components/catalog/CatalogFilterRail";

const SEARCH_DEBOUNCE_MS = 300;

export const MyObjects = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { profile, displayName } = useUserProfile();
  const { activeCatalogOwnerId, activeCatalogAliId, activeDelegation, isViewingDelegatedCatalog, ownedCatalogs } = useActiveCatalog();
  // When the user picks one of their own add-on catalogs, only
  // activeCatalogAliId changes — activeCatalogOwnerId stays null and
  // isViewingDelegatedCatalog stays false. Resolve the active owned-catalog
  // row so the heading/ALI line reflect the selection instead of always
  // showing the user's primary profile.
  const activeOwnedCatalog = useMemo(
    () =>
      !isViewingDelegatedCatalog && activeCatalogAliId
        ? ownedCatalogs.find(c => c.catalogAliId === activeCatalogAliId) ?? null
        : null,
    [isViewingDelegatedCatalog, activeCatalogAliId, ownedCatalogs]
  );
  const showOwnedAddonHeading = activeOwnedCatalog !== null && !activeOwnedCatalog.isPrimary;
  const canEditDelegatedCatalog = activeDelegation?.permissions === 'full';
  const { toggleBookmark, isBookmarked } = useBookmarks();
  // `activeCatalogCollections` is the album set for the catalog being viewed
  // (robustly scoped to the active owner, sourced from the cross-catalog union),
  // so the per-object "Catalog / album" dropdown shows the viewed catalog's
  // albums — not the caller's own when viewing a delegated catalog.
  const { selectedCollectionId, fetchCollectionItems, selectCollection, activeCatalogCollections, allAccessibleCollections, removeItemFromCollection, membershipVersion } = useCollections();
  // Stable references: the `?? []` fallbacks must not allocate a fresh array each
  // render, or downstream useMemo/useBrowse deps thrash into refetch loops.
  const EMPTY_COLLECTIONS = useMemo<CollectionWithRole[]>(() => [], []);
  const collections = activeCatalogCollections ?? EMPTY_COLLECTIONS;
  const unionCollections = allAccessibleCollections ?? EMPTY_COLLECTIONS;

  // GH#1265: server-side facet filter rail + search, behind
  // enableCatalogFilters. Off in prod until validated on test — flag OFF
  // must preserve today's behavior exactly (no rail, no search box, no
  // facet/q params sent to useBrowse).
  const catalogFiltersEnabled = featureFlags.isEnabled('enableCatalogFilters');
  // Filters (and, below, search) must clear whenever the browsing scope
  // changes: switching between own/add-on/delegated catalogs, or albums.
  const catalogFilterScopeKey = `${activeCatalogOwnerId ?? ''}|${activeCatalogAliId ?? ''}|${selectedCollectionId ?? ''}`;
  const {
    selected: selectedFacets,
    toggle: toggleFacet,
    clearAll: clearAllFacets,
    filtersActive,
    browseParams: facetBrowseParams,
  } = useCatalogFacetFilters(catalogFilterScopeKey);

  // Free-text search (GH#1265: keywords ride FTS `q` — facets alone don't
  // cover them). Mirrors PublicCatalog's debounce; also resets to '' — with
  // no wait for the debounce timer — whenever the scope changes, via the
  // same "adjust state during render" pattern useCatalogFacetFilters uses
  // internally for its own reset.
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [prevSearchScopeKey, setPrevSearchScopeKey] = useState(catalogFilterScopeKey);
  if (catalogFilterScopeKey !== prevSearchScopeKey) {
    setPrevSearchScopeKey(catalogFilterScopeKey);
    setSearchText('');
    setDebouncedSearchText('');
  }

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Single trimmed derivation of the search text: the SAME value must feed
  // the browse `q` param, the filters-active flag, and the selection reset
  // key — otherwise whitespace-only input sends q=" " while reporting
  // "not filtering" (review finding: a zero-match response would then show
  // the upload-CTA instead of the filtered-empty state).
  const effectiveSearchText = debouncedSearchText.trim();

  // Whether the rail's filters or the search box are actively narrowing
  // the result set. Only meaningful when the rail is enabled. Declared
  // before the unfiltered-total tracker below, which must not run while
  // filtering.
  const filtersOrSearchActive = catalogFiltersEnabled && (filtersActive || effectiveSearchText !== '');

  // Collection item IDs for server-side filtering
  const [collectionItemIds, setCollectionItemIds] = useState<string[] | undefined>(undefined);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // Track the unfiltered catalog total separately so the "Catalog (N)" dropdown
  // option always shows the full count even when an album filter is active.
  // browseTotal reflects the collection-scoped count when itemIds are passed to
  // useBrowse, so without this the "Catalog" option would read "Catalog (2)"
  // when browsing an album with 2 items in a 2308-item catalog (issue #1159).
  const [unfilteredCatalogTotal, setUnfilteredCatalogTotal] = useState(0);

  // Sort state with localStorage persistence
  const [sortOption, setSortOption] = useState<SortOption>(() => loadMyObjectsSortPreference());

  // View mode state with localStorage persistence
  const [viewMode, setViewMode] = useState<ViewMode>(() => loadViewMode());

  // Page size state with localStorage persistence
  const PAGE_SIZE_OPTIONS = [12, 24, 48, 96];
  const PAGE_SIZE_KEY = 'legacy-catalog-page-size';
  const [pageSize, setPageSize] = useState<number>(() => {
    const stored = localStorage.getItem(PAGE_SIZE_KEY);
    const parsed = stored ? parseInt(stored, 10) : 12;
    return PAGE_SIZE_OPTIONS.includes(parsed) ? parsed : 12;
  });

  const handlePageSizeChange = useCallback((e: SelectChangeEvent) => {
    const size = parseInt(e.target.value, 10);
    setPageSize(size);
    localStorage.setItem(PAGE_SIZE_KEY, String(size));
  }, []);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    saveViewMode(mode);
  }, []);

  const handleSortChange = useCallback((option: SortOption) => {
    setSortOption(option);
    saveMyObjectsSortPreference(option);
  }, []);

  // Get the name of the currently selected collection
  const selectedCollectionName = useMemo(() => {
    if (!selectedCollectionId) return null;
    // Resolve from the union so the name shows even if the selection briefly
    // isn't in the active-catalog-filtered list (e.g. mid owner-resolution).
    const collection =
      collections.find(c => c.collectionId === selectedCollectionId) ??
      unionCollections.find(c => c.collectionId === selectedCollectionId);
    return collection?.name || null;
  }, [selectedCollectionId, collections, unionCollections]);

  // Fetch collection membership IDs when selected collection changes
  useEffect(() => {
    const loadCollectionItemIds = async () => {
      if (selectedCollectionId) {
        setCatalogLoading(true);
        try {
          const memberships = await fetchCollectionItems(selectedCollectionId);
          setCollectionItemIds(memberships.map(m => m.catalogItemId));
        } catch (err: unknown) {
          const fetchErr = err as { response?: { status?: number } };
          if (fetchErr.response?.status === 404) {
            selectCollection(null);
            localStorage.removeItem('selectedCollectionId');
          }
          setCollectionItemIds(undefined);
        } finally {
          setCatalogLoading(false);
        }
      } else {
        setCollectionItemIds(undefined);
        setCatalogLoading(false);
      }
    };

    loadCollectionItemIds();
  }, [selectedCollectionId, fetchCollectionItems, selectCollection, membershipVersion]);

  // #1201: when a collection (album) is selected, ALWAYS pass a defined array
  // to useBrowse — never `undefined` — so an empty or not-yet-loaded album
  // filters to ZERO results instead of falling through to the full catalog.
  // `undefined` is reserved for "no album selected" (full catalog). Memoised so
  // the `?? []` fallback doesn't allocate a fresh array each render (which would
  // thrash useBrowse's fetch effect into an infinite refetch loop).
  const collectionFilterItemIds = useMemo(
    () => (selectedCollectionId ? (collectionItemIds ?? []) : undefined),
    [selectedCollectionId, collectionItemIds]
  );

  // Server-side browse via OpenSearch. Facet/search params are only sent
  // when the rail is enabled — flag OFF must produce byte-identical
  // requests to before this MR (no q/workType/materials/.../facets params
  // at all). Facets compose server-side with ownerOnly/ownerId/catalogAliId/
  // itemIds — no client-side filtering anywhere.
  const {
    items: browseItems,
    total: browseTotal,
    page: browsePage,
    setPage: setBrowsePage,
    loading,
    error,
    totalPages,
    refetch,
    removeItems,
    facetBuckets,
  } = useBrowse({
    sortBy: toSearchSortBy(sortOption),
    pageSize,
    ownerOnly: true,
    ownerId: activeCatalogOwnerId || undefined,
    // PR 4b: own add-on switching. activeCatalogOwnerId stays null when the
    // caller picks one of their own catalogs (primary or add-on), so without
    // catalogAliId useBrowse can't tell the selection changed.
    catalogAliId: activeCatalogOwnerId ? undefined : (activeCatalogAliId || undefined),
    itemIds: collectionFilterItemIds,
    ...(catalogFiltersEnabled
      ? {
          q: effectiveSearchText || undefined,
          workType: facetBrowseParams.workType,
          materials: facetBrowseParams.materials,
          techniques: facetBrowseParams.techniques,
          creators: facetBrowseParams.creators,
          decade: facetBrowseParams.decade,
          facets: ALL_CATALOG_FACETS,
        }
      : {}),
  });

  // Build browse query for cross-page boundary navigation on the detail page
  // (GL#39). Must mirror the params passed to useBrowse above so the boundary
  // fetch retrieves items in the same scope/sort/order. Collection filter IDs
  // are carried as filterItemIds (renamed to avoid collision with the nav
  // state's top-level itemIds field).
  const browseQuery = useMemo((): BrowseNavQuery => ({
    sortBy: toSearchSortBy(sortOption),
    ownerOnly: true,
    ownerId: activeCatalogOwnerId || undefined,
    catalogAliId: activeCatalogOwnerId ? undefined : (activeCatalogAliId || undefined),
    filterItemIds: selectedCollectionId ? collectionItemIds : undefined,
  }), [sortOption, activeCatalogOwnerId, activeCatalogAliId, selectedCollectionId, collectionItemIds]);

  // Keep the unfiltered total up to date whenever the full catalog is being
  // browsed (no collection active). This ensures the "Catalog (N)" option in
  // the collection dropdown always reflects the complete count even when the
  // user subsequently selects an album (issue #1159).
  //
  // GH#1265 review: with the filter rail/search active, browseTotal is the
  // FILTERED count — it must not overwrite the true catalog total (which
  // also feeds the export dialog's item count), so the tracker only runs
  // when nothing is narrowing the result set.
  useEffect(() => {
    if (!selectedCollectionId && !filtersOrSearchActive && browseTotal > 0) {
      setUnfilteredCatalogTotal(browseTotal);
    }
  }, [selectedCollectionId, filtersOrSearchActive, browseTotal]);

  // When sorting by inventory number, display the inventory number on each
  // card in place of the ALI catalog number. The sort selection is the
  // implicit toggle — no separate control needed.
  const showInventoryNumber =
    sortOption === 'custom_asc' || sortOption === 'custom_desc';

  const hasError = error;

  // Whether to show the filtered-empty state (distinct from the genuinely
  // empty catalog / empty album): only when the rail is enabled and a
  // filter or search term is actively narrowing (filtersOrSearchActive,
  // declared above the unfiltered-total tracker).
  const showFilteredEmpty = filtersOrSearchActive && browseItems.length === 0;

  const handleClearCatalogFilters = useCallback(() => {
    clearAllFacets();
    setSearchText('');
    setDebouncedSearchText('');
  }, [clearAllFacets]);

  // Bulk selection must reset whenever the effective query changes — not
  // just page/collection/sort but also facet selections and search text
  // (parked-branch review finding, GH#1265): stale selections from a
  // pre-filter result set must not survive into a filtered one. Facet
  // values are canonicalized (sorted) so toggle order alone never
  // spuriously changes the key when the actual selection set is unchanged.
  const serializedFacetSelection = useMemo(
    () =>
      JSON.stringify(
        ALL_CATALOG_FACETS.reduce<Record<string, string[]>>((acc, key) => {
          acc[key] = [...facetBrowseParams[key]].sort();
          return acc;
        }, {})
      ),
    [facetBrowseParams]
  );

  // Declared early so handleExportPdf (below) can read selectedIds.
  const selectionResetKey = `${browsePage}-${selectedCollectionId}-${sortOption}-${serializedFacetSelection}-${effectiveSearchText}`;
  const {
    isSelected,
    toggle: toggleSelect,
    selectAll,
    clearAll: clearSelection,
    isAllSelected,
    isAnySelected,
    selectedCount,
    selectedIds,
  } = useSelection(selectionResetKey);

  const handleExportPdf = useCallback(async (includePrivateFields: boolean, onProgress?: PdfProgressCallback) => {
    const selectedArray = Array.from(selectedIds);
    const items = await fetchPdfData(
      selectedCollectionId || undefined,
      includePrivateFields,
      activeCatalogOwnerId || undefined,
      selectedArray.length > 0 ? selectedArray : undefined
    );

    const pdfItems: PdfExportItem[] = items.map((item) => ({
      aliCatalogNumber: item.ali_catalog_number,
      title: item.title,
      creators: item.creators,
      creationDate: item.creation_date,
      workType: item.work_type,
      techniques: item.techniques,
      materials: item.materials,
      mediaDescription: item.media_description || '',
      dimensions: item.dimensions,
      keywords: item.keywords,
      publicNotes: item.public_notes,
      privateNotes: item.private_notes || '',
      physicalLocation: item.physical_location || '',
      estimatedValue: item.estimated_value || '',
      provenanceRecords: item.provenance_records,
      editions: item.editions,
      exhibitions: item.exhibitions,
      publications: item.publications || '',
      awards: item.awards || '',
      customCatalogNumbers: item.custom_catalog_numbers || '',
      alternativeTitles: item.alternative_titles || '',
      substrate: item.substrate || '',
      imageUrl: item.image_url || undefined,
    }));

    await generateCatalogPdf(pdfItems, { includePrivateFields }, onProgress);
  }, [selectedCollectionId, activeCatalogOwnerId, selectedIds]);

  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [removeFromCollectionDialog, setRemoveFromCollectionDialog] = useState<{
    open: boolean;
    artworkId: string;
    title: string;
  }>({ open: false, artworkId: '', title: '' });
  const [removeFromCollectionLoading, setRemoveFromCollectionLoading] = useState(false);
  // Handle navigation state from ObjectDetails after single-item deletion
  const ALLOWED_NAV_MESSAGES = useMemo(() => new Set([
    'Object deleted successfully',
    'Item cleared for reuse',
  ]), []);

  useEffect(() => {
    const state = location.state as Record<string, unknown> | null;
    if (!state) return;

    // Handle deletion snackbar from ObjectDetails
    const deletedItemId = typeof state.deletedItemId === 'string' ? state.deletedItemId : undefined;
    const snackbar = typeof state.snackbar === 'string' ? state.snackbar : undefined;
    if (deletedItemId) {
      removeItems([deletedItemId]);
    }

    // Handle cleared-for-reuse navigation from ReviewTab. Unlike delete,
    // cleared items remain visible in the list (retired items are always
    // shown), so we refetch instead of optimistically removing. A second
    // refetch after ~2s covers the OpenSearch indexer lag window.
    const clearedItemId = typeof state.clearedItemId === 'string' ? state.clearedItemId : undefined;
    const cleanupClearedRefetch = handleClearedItemNavState({
      clearedItemId,
      refetch,
      removeItems,
    });

    if (snackbar && ALLOWED_NAV_MESSAGES.has(snackbar)) {
      setSnackbarMessage(snackbar);
      setSnackbarOpen(true);
    }

    // Restore browse page from back navigation (e.g., returning from ObjectDetails)
    const savedPage = typeof state.browsePage === 'number' ? state.browsePage : undefined;
    if (savedPage != null && savedPage >= 0) {
      setBrowsePage(savedPage);
    }

    // "Back to catalog" from ObjectDetails always resets the collection filter
    // so the full catalog is shown instead of being stuck in an album view
    // (issue #1159 back-navigation variant).
    if (state.resetCollection === true) {
      selectCollection(null);
    }

    // Clear navigation state to prevent re-trigger on back navigation
    navigate(location.pathname, { replace: true, state: null });

    return cleanupClearedRefetch;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once on mount — reads all state before clearing

  // Auto-refresh when items are still processing images
  useEffect(() => {
    const hasProcessingItems = browseItems.some(
      item => item.processingStatus === 'UPLOADING' || item.processingStatus === 'PROCESSING'
    );
    if (!hasProcessingItems) return;

    const intervalId = setInterval(() => {
      refetch();
    }, 5000);

    return () => clearInterval(intervalId);
  }, [browseItems, refetch]);

  // Browse items from server-side pagination. Retired items (cleared, deleted,
  // duplicate) are always shown as dimmed placeholders, so no filtering here.
  const browseItemIds = useMemo(() => browseItems.map(i => i.catalogItemId), [browseItems]);

  const gridColumns = useMemo(() => {
    if (isMobile) return "repeat(2, 1fr)";
    if (isTablet) return "repeat(3, 1fr)";
    if (isLaptop) return "repeat(4, 1fr)";
    return "repeat(6, 1fr)";
  }, [isMobile, isTablet, isLaptop]);

  const shareUrl = window.location.href;

  const handleShare = () => {
    setShareModalOpen(true);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setSnackbarMessage('Link copied to clipboard!');
    setSnackbarOpen(true);
    setShareModalOpen(false);
  };

  const handleEditArtwork = (artworkId: string) => {
    const ownerSuffix = activeCatalogOwnerId ? `&owner=${activeCatalogOwnerId}` : '';
    navigate(`/upload-object?edit=${artworkId}${ownerSuffix}`, { state: { browsePage } });
  };

  const handleRemoveFromCollectionClick = (artworkId: string, title: string) => {
    setRemoveFromCollectionDialog({ open: true, artworkId, title });
  };

  const handleRemoveFromCollectionConfirm = async () => {
    if (!selectedCollectionId || !removeFromCollectionDialog.artworkId) return;

    setRemoveFromCollectionLoading(true);
    try {
      await removeItemFromCollection(selectedCollectionId, removeFromCollectionDialog.artworkId);
      setRemoveFromCollectionDialog({ open: false, artworkId: '', title: '' });
      setSnackbarMessage('Item removed from album');
      setSnackbarOpen(true);
    } catch (err) {
      logger.error('Failed to remove item from collection', err);
      setSnackbarMessage('Failed to remove item from album');
      setSnackbarOpen(true);
    } finally {
      setRemoveFromCollectionLoading(false);
    }
  };

  // Collection selector change handler
  const handleCollectionChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    selectCollection(value === 'all' ? null : value);
  };

  // Determine the collection heading display
  const collectionHeadingLabel = selectedCollectionName
    ? `${getCatalogDisplayName(selectedCollectionName)} (${browseTotal})`
    : `${en.catalogSwitcher.catalog} (${browseTotal})`;

  const canEdit = !isViewingDelegatedCatalog || canEditDelegatedCatalog;

  // Selection mode is active whenever canEdit is true (checkboxes always available for owners)
  const selectionMode = canEdit;

  const handleBulkMakePublic = useCallback(async () => {
    try {
      await updateVisibilityBulk(
        Array.from(selectedIds),
        'public',
        activeCatalogOwnerId
      );
      clearSelection();
      setSnackbarMessage(`${selectedCount} items set to public`);
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('Failed to update visibility');
      setSnackbarOpen(true);
    }
  }, [selectedIds, selectedCount, activeCatalogOwnerId, clearSelection]);

  const handleBulkMakePrivate = useCallback(async () => {
    try {
      await updateVisibilityBulk(
        Array.from(selectedIds),
        'private',
        activeCatalogOwnerId
      );
      clearSelection();
      setSnackbarMessage(`${selectedCount} items set to private`);
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('Failed to update visibility');
      setSnackbarOpen(true);
    }
  }, [selectedIds, selectedCount, activeCatalogOwnerId, clearSelection]);

  const [bulkEditOpen, setBulkEditOpen] = useState(false);

  const handleBulkEditApplied = useCallback(
    (summary: BulkEditResponse) => {
      setBulkEditOpen(false);
      clearSelection();
      const failedSuffix = summary.failed > 0 ? `, ${summary.failed} failed` : '';
      setSnackbarMessage(`Bulk edit applied: ${summary.updated} updated${failedSuffix}`);
      setSnackbarOpen(true);
      // Re-read the grid so the edited metadata (titles, work type, etc.) and
      // any derived display fields reflect the new values.
      refetch();
    },
    [clearSelection, refetch]
  );


  // Determine grid container styles based on view mode
  const isEmptyState = authLoading || loading || !isAuthenticated || hasError || browseItems.length === 0;
  const gridContainerSx = viewMode === 'grid' && !isEmptyState
    ? {
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gridAutoRows: 'auto',
        alignItems: 'stretch',
      }
    : {
        display: 'flex',
        flexDirection: isEmptyState ? undefined : 'column',
        alignItems: isEmptyState ? 'center' : undefined,
        justifyContent: isEmptyState ? 'center' : undefined,
        minHeight: isEmptyState ? 300 : undefined,
      };

  // Results column: bulk-actions toolbar, catalog content area, pagination.
  // Extracted so the flag-off branch of the render can mount it with ZERO
  // extra wrapper DOM (GH#1265 review: flag off must be DOM-identical to
  // the pre-rail page), while the flag-on branch nests it beside the rail.
  const resultsColumn = (
    <>
      {/* Bulk actions toolbar — shown when items are selected */}
      {isAnySelected && (
        <Box sx={{ mx: { xs: 1, sm: 0 } }}>
          <BulkActionsToolbar
            selectedCount={selectedCount}
            totalCount={browseItems.length}
            isAllSelected={isAllSelected(browseItemIds)}
            onSelectAll={() => selectAll(browseItemIds)}
            onClearAll={clearSelection}
            onMakePublic={handleBulkMakePublic}
            onMakePrivate={handleBulkMakePrivate}
            onBulkEdit={() => setBulkEditOpen(true)}
          />
        </Box>
      )}

      {/* Catalog content area */}
      <Box
        sx={{
          bgcolor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          position: "relative",
          ...gridContainerSx,
          mx: { xs: 1, sm: 0 }
        }}
      >
        {(authLoading || loading || catalogLoading) && browseItems.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {authLoading ? en.home.checkingAuth : en.home.loadingArtwork}
            </Typography>
          </Box>
        ) : !isAuthenticated ? (
          <Box sx={{ textAlign: "center", p: 4 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {en.home.pleaseSignIn}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {en.home.signInDescription}
            </Typography>
            <AppButton variant="primary" onClick={() => navigate("/auth")}>
              {en.home.signIn}
            </AppButton>
          </Box>
        ) : hasError ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              (Network Error: {error})
            </Typography>
          </Box>
        ) : browseItems.length === 0 ? (
          showFilteredEmpty ? (
            <Box sx={{ textAlign: "center", p: 4 }} data-testid="myobjects-filtered-empty">
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No objects match your filters
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Try clearing a filter or search term to see more objects.
              </Typography>
              <Button variant="outlined" onClick={handleClearCatalogFilters}>
                Clear filters
              </Button>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {selectedCollectionId ? en.collections.emptyCollection : en.home.noArtworkYet}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {selectedCollectionId
                  ? en.collections.emptyCollectionDescription
                  : en.home.noArtworkDescription}
              </Typography>
              {!selectedCollectionId && (
                <AppButton
                  variant="primary"
                  onClick={() => navigate("/upload-object")}
                >
                  {en.home.uploadFirstArtwork}
                </AppButton>
              )}
            </Box>
          )
        ) : (
          <>
            {viewMode === 'grid' && (
              <CatalogGridView
                items={browseItems}
                gridColumns={gridColumns}
                isBookmarked={isBookmarked}
                toggleBookmark={toggleBookmark}
                onEdit={handleEditArtwork}
                onRemoveFromCollection={handleRemoveFromCollectionClick}
                selectedCollectionId={selectedCollectionId}
                canEdit={canEdit}
                browseItemIds={browseItemIds}
                routePrefix="/object-details"
                browseTotal={browseTotal}
                browsePageOffset={browsePage * pageSize}
                browsePage={browsePage}
                browseQuery={browseQuery}
                browsePageSize={pageSize}
                isSelected={isSelected}
                onToggleSelect={toggleSelect}
                selectionMode={selectionMode}
                showInventoryNumber={showInventoryNumber}
              />
            )}
            {viewMode === 'list' && (
              <CatalogListView
                items={browseItems}
                isBookmarked={isBookmarked}
                toggleBookmark={toggleBookmark}
                onEdit={handleEditArtwork}
                onRemoveFromCollection={handleRemoveFromCollectionClick}
                selectedCollectionId={selectedCollectionId}
                canEdit={canEdit}
                browseItemIds={browseItemIds}
                routePrefix="/object-details"
                browseTotal={browseTotal}
                browsePageOffset={browsePage * pageSize}
                browsePage={browsePage}
                browseQuery={browseQuery}
                browsePageSize={pageSize}
                isSelected={isSelected}
                onToggleSelect={toggleSelect}
                selectionMode={selectionMode}
                showInventoryNumber={showInventoryNumber}
              />
            )}
            {viewMode === 'image' && (
              <CatalogImageView
                items={browseItems}
                isBookmarked={isBookmarked}
                toggleBookmark={toggleBookmark}
                onEdit={handleEditArtwork}
                onRemoveFromCollection={handleRemoveFromCollectionClick}
                selectedCollectionId={selectedCollectionId}
                canEdit={canEdit}
                browseItemIds={browseItemIds}
                routePrefix="/object-details"
                browseTotal={browseTotal}
                browsePageOffset={browsePage * pageSize}
                browsePage={browsePage}
                browseQuery={browseQuery}
                browsePageSize={pageSize}
                isSelected={isSelected}
                onToggleSelect={toggleSelect}
                selectionMode={selectionMode}
              />
            )}
          </>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <CatalogPagination
            count={totalPages}
            page={browsePage + 1}
            onChange={(_event, p) => setBrowsePage(p - 1)}
            color="primary"
          />
        </Box>
      )}
    </>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
      <DisplayNameNotice />
      {isViewingDelegatedCatalog && activeDelegation && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'info.main', color: 'info.contrastText', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography>
            {en.catalogSwitcher.delegatedBanner
              .replace('{aliId}', activeDelegation.delegatorAliId)
              .replace('{permission}', activeDelegation.permissions)}
          </Typography>
        </Box>
      )}
      {/* Heading + ALI ID */}
      <Box sx={{ px: { xs: 1, sm: 0 } }}>
        <Typography
          variant="h1"
          component="h1"
          sx={{
            color: theme.palette.primary.main,
            fontFamily: "Libre Baskerville, serif",
            fontWeight: "bold",
            fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.2rem' },
            whiteSpace: 'nowrap',
          }}
        >
          {isViewingDelegatedCatalog && activeDelegation
            ? `${activeDelegation.delegatorName || activeDelegation.delegatorAliId}'s ${en.catalogSwitcher.catalog}`
            : showOwnedAddonHeading
              ? `${activeOwnedCatalog!.creatorName} ${en.catalogSwitcher.catalog}`.trim()
              : `${getArtistDisplayName(displayName)} ${en.catalogSwitcher.catalog}`.trim()}
        </Typography>
        {isViewingDelegatedCatalog && activeDelegation ? (
          activeDelegation.delegatorName && (
            <Typography variant="body2" color="text.secondary">
              {activeDelegation.delegatorAliId}
            </Typography>
          )
        ) : showOwnedAddonHeading ? (
          <Typography variant="body2" color="text.secondary">
            {activeOwnedCatalog!.catalogAliId}
          </Typography>
        ) : (
          profile?.aliId && (
            <Typography variant="body2" color="text.secondary">
              {profile.aliId}
            </Typography>
          )
        )}
      </Box>

      {/* Collection selector + controls toolbar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: { xs: 1, sm: 2 },
          flexWrap: "wrap",
          px: { xs: 1, sm: 0 }
        }}
      >
        {/* Left: collection selector heading */}
        <Select
          value={selectedCollectionId || 'all'}
          onChange={handleCollectionChange}
          variant="standard"
          disableUnderline
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
            '& .MuiSelect-select': {
              py: 0.5,
            },
          }}
          renderValue={() => collectionHeadingLabel}
        >
          <MenuItem value="all">
            <Typography>{en.catalogSwitcher.catalog} ({unfilteredCatalogTotal || browseTotal})</Typography>
          </MenuItem>
          {collections.map(collection => (
            <MenuItem key={collection.collectionId} value={collection.collectionId}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <Typography>{getCatalogDisplayName(collection.name)}</Typography>
                <Typography variant="caption" color="text.secondary" ml="auto">
                  ({collection.itemCount} items)
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>

        {/* Right: controls */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          {/* Add New Object button */}
          {canEdit && (
            <AppButton
              variant="primary"
              onClick={() => navigate("/upload-object")}
            >
              {en.app.addNewObject}
            </AppButton>
          )}

          {/* Sort selector */}
          <SortSelector
            value={sortOption}
            onChange={handleSortChange}
            compact={isMobile}
            sortOptions={MY_OBJECTS_SORT_OPTIONS}
          />

          {/* Page size selector */}
          <Select
            value={String(pageSize)}
            onChange={handlePageSizeChange}
            size="small"
            sx={{ minWidth: 80 }}
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <MenuItem key={n} value={String(n)}>{n}</MenuItem>
            ))}
          </Select>

          {/* View mode toggle */}
          <ViewModeToggle value={viewMode} onChange={handleViewModeChange} />

          {/* Actions dropdown */}
          <Select
            value=""
            onChange={(e: SelectChangeEvent) => {
              const action = e.target.value;
              if (action === 'export') setExportDialogOpen(true);
              if (action === 'share') handleShare();
              if (action === 'bulkUpload') navigate('/bulk-upload');
              if (action === 'viewPublic') {
                if (isViewingDelegatedCatalog && activeDelegation) {
                  navigate(`/public/catalog/${activeDelegation.delegatorAliId}?from=upload`);
                } else {
                  navigate(
                    profile?.aliId
                      ? `/public/catalog/${profile.aliId}?from=upload`
                      : '/public/catalog?from=upload'
                  );
                }
              }
            }}
            size="small"
            displayEmpty
            renderValue={() => 'Actions'}
            sx={{ minWidth: isMobile ? 100 : 140 }}
          >
            <MenuItem value="export">
              <Download sx={{ mr: 1, fontSize: 20 }} /> Export
            </MenuItem>
            <MenuItem value="share">
              <Share sx={{ mr: 1, fontSize: 20 }} /> Share
            </MenuItem>
            {/* Write action — hidden from view-only delegates, matching the
                "Add New Object" gate above (#1237 review). */}
            {canEdit && (
              <MenuItem value="bulkUpload">
                <CloudUpload sx={{ mr: 1, fontSize: 20 }} /> {en.app.bulkUpload}
              </MenuItem>
            )}
            <MenuItem value="viewPublic">
              <PublicIcon sx={{ mr: 1, fontSize: 20 }} /> {en.app.viewAsPublic}
            </MenuItem>
          </Select>
        </Box>
      </Box>

      {/* Filter rail + results column (GH#1265). Flag ON nests the results
          column beside the rail with a search box on top, mirroring
          PublicCatalog's layout (GH#1263); flag OFF mounts resultsColumn
          directly so the DOM is identical to the pre-rail page. */}
      {catalogFiltersEnabled ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '288px 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <CatalogFilterRail
            buckets={facetBuckets}
            selected={selectedFacets}
            onToggle={toggleFacet}
            onClearAll={clearAllFacets}
          />

          <Box sx={{ minWidth: 0 }}>
            {/* Free-text search (GH#1265) */}
            <Box sx={{ mb: 2, mx: { xs: 1, sm: 0 } }}>
              <TextField
                placeholder="Search titles, keywords, creators, materials..."
                size="small"
                fullWidth
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                inputProps={{ 'aria-label': 'Search my objects' }}
              />
            </Box>

            {resultsColumn}
          </Box>
        </Box>
      ) : (
        resultsColumn
      )}

      <BulkEditDialog
        open={bulkEditOpen}
        selectedIds={Array.from(selectedIds)}
        ownerId={activeCatalogOwnerId}
        onClose={() => setBulkEditOpen(false)}
        onApplied={handleBulkEditApplied}
      />

      {/* Remove from Collection Confirmation Dialog */}
      <Dialog
        open={removeFromCollectionDialog.open}
        onClose={() => setRemoveFromCollectionDialog({ open: false, artworkId: '', title: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Remove from Album</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>&quot;{removeFromCollectionDialog.title || 'this item'}&quot;</strong> from the current album?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            The item will remain in your catalog but will no longer be part of this collection.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoveFromCollectionDialog({ open: false, artworkId: '', title: '' })}
            disabled={removeFromCollectionLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveFromCollectionConfirm}
            color="error"
            variant="contained"
            disabled={removeFromCollectionLoading}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={shareModalOpen} onClose={() => setShareModalOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ position: 'relative', pr: 5 }}>
          Share Catalog
          <IconButton onClick={() => setShareModalOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Share Link"
            value={shareUrl}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={handleCopyLink}>
                  <ContentCopy />
                </IconButton>
              ),
            }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      {/* itemCount must be the server-side catalog total, not the current-page
          length (browseItems.length), so the dialog displays and exports
          the entire catalog rather than just the visible page. (#1152) */}
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        collectionId={selectedCollectionId || undefined}
        itemCount={selectedCollectionId ? browseTotal : (unfilteredCatalogTotal || browseTotal)}
        onExportPdf={handleExportPdf}
        ownerId={activeCatalogOwnerId || undefined}
        selectedIds={Array.from(selectedIds)}
      />

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};
